import apiClient from './api';
import { ApiResponse } from '@/types';

export interface PaymentMethodInfo {
  id: string;
  organization_id: string;
  provider: string;
  provider_method_id: string;
  type: string;
  last_four: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PaymentConfig {
  publishable_key: string;
  provider: string;
}

class PaymentService {
  async getConfig(): Promise<PaymentConfig> {
    const response = await apiClient.get<ApiResponse<PaymentConfig>>('/payments/config');
    return response.data.data!;
  }

  async listMethods(): Promise<PaymentMethodInfo[]> {
    const response = await apiClient.get<ApiResponse<PaymentMethodInfo[]>>('/payments/methods');
    return response.data.data || [];
  }

  async createSetupIntent(): Promise<{ client_secret: string; setup_intent_id: string }> {
    const response = await apiClient.post<ApiResponse<{ client_secret: string; setup_intent_id: string }>>('/payments/setup-intent');
    return response.data.data!;
  }

  async confirmMethod(providerMethodId: string): Promise<PaymentMethodInfo> {
    const response = await apiClient.post<ApiResponse<PaymentMethodInfo>>('/payments/methods/confirm', {
      provider_method_id: providerMethodId,
    });
    return response.data.data!;
  }

  async setDefault(methodId: string): Promise<void> {
    await apiClient.put(`/payments/methods/${methodId}/default`);
  }

  async removeMethod(methodId: string): Promise<void> {
    await apiClient.delete(`/payments/methods/${methodId}`);
  }

  async payInvoice(invoiceId: string): Promise<{ status: string; paymentIntentId: string }> {
    const response = await apiClient.post<ApiResponse<{ status: string; paymentIntentId: string }>>(`/payments/invoices/${invoiceId}/pay`);
    return response.data.data!;
  }

  async purchaseCredits(amount: number, methodId?: string): Promise<{ credits: number; paymentIntentId: string; discount?: { amount: number; couponCode: string } }> {
    const response = await apiClient.post<ApiResponse<{ credits: number; paymentIntentId: string; discount?: { amount: number; couponCode: string } }>>('/payments/credits/purchase', {
      amount,
      method_id: methodId,
    });
    return response.data.data!;
  }

  async previewCreditDiscount(amount: number): Promise<{
    applicable: boolean;
    discountAmount: number;
    chargeAmount: number;
    creditAmount: number;
    couponCode?: string;
    reason?: string;
  }> {
    const response = await apiClient.post<ApiResponse<{
      applicable: boolean;
      discountAmount: number;
      chargeAmount: number;
      creditAmount: number;
      couponCode?: string;
      reason?: string;
    }>>('/payments/credits/preview-discount', { amount });
    return response.data.data!;
  }

  async hasMethod(): Promise<boolean> {
    const response = await apiClient.get<ApiResponse<{ has_method: boolean }>>('/payments/has-method');
    return response.data.data?.has_method || false;
  }
}

export const paymentService = new PaymentService();
export default paymentService;
